import { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import TableWithPagination from '@common/components/TableWithPagination';
import { ChevronIcon } from '@common/components/icons';
import * as styles from './index.module.css';

/** Expand/collapse toggle rendered in the leading column of each row. */
function ExpandToggle({ open, onToggle, expandLabel, collapseLabel }) {
  return (
    <button
      type="button"
      className={`${styles.expandButton} ${open ? styles.expandOpen : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-expanded={open}
      title={open ? collapseLabel : expandLabel}
    >
      <ChevronIcon width="10" height="6" />
    </button>
  );
}

ExpandToggle.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  expandLabel: PropTypes.string.isRequired,
  collapseLabel: PropTypes.string.isRequired,
};

/**
 * Reusable table with per-row drill-down. Wraps the shared TableWithPagination and
 * adds a leading expand-toggle column; clicking it expands a detail panel (rendered
 * by `renderDetail`) beneath the row. All other props are forwarded to the table.
 *
 * @param {(row: object) => React.ReactNode} renderDetail - Detail content for an expanded row.
 * @param {(row: object) => (string|number)} getRowId - Stable id per row (required).
 * @param {Array} columns - Column defs (the expand column is prepended automatically).
 * @param {string} [expandLabel] - Accessible label for the expand toggle (pass a translated string).
 * @param {string} [collapseLabel] - Accessible label when expanded.
 */
export default function ExpandableTable({
  columns,
  getRowId,
  renderDetail,
  expandLabel,
  collapseLabel,
  ...tableProps
}) {
  const [expandedIds, setExpandedIds] = useState({});

  const toggle = useCallback((id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const isRowExpanded = useCallback((row) => !!expandedIds[getRowId(row)], [expandedIds, getRowId]);

  const expandColumn = useMemo(
    () => ({
      id: 'expand',
      staticWidth: 40,
      enableResizing: false,
      enableSorting: false,
      meta: { label: expandLabel },
      // Column cell renderer (TanStack pattern), not a component definition.
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => (
        <ExpandToggle
          open={!!expandedIds[getRowId(row.original)]}
          onToggle={() => toggle(getRowId(row.original))}
          expandLabel={expandLabel}
          collapseLabel={collapseLabel}
        />
      ),
    }),
    [expandedIds, getRowId, toggle, expandLabel, collapseLabel],
  );

  const columnsWithExpand = useMemo(() => [expandColumn, ...columns], [expandColumn, columns]);

  return (
    <TableWithPagination
      {...tableProps}
      columns={columnsWithExpand}
      getRowId={getRowId}
      isRowExpanded={isRowExpanded}
      renderExpanded={renderDetail}
    />
  );
}

ExpandableTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getRowId: PropTypes.func.isRequired,
  renderDetail: PropTypes.func.isRequired,
  expandLabel: PropTypes.string,
  collapseLabel: PropTypes.string,
};

ExpandableTable.defaultProps = {
  expandLabel: 'Expand',
  collapseLabel: 'Collapse',
};
